// Nexus shared layout (NavBar + Footer) + Owner mode switcher
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Hexagon, Compass, Tv, Wallet, Crown, LogOut, Radio, Sparkles, Eye, Trophy, MessageCircle, Shield, Briefcase, User as UserIcon, Megaphone, Settings } from "lucide-react";
import PasswordGate from "./PasswordGate";
import EdenChat from "./EdenChat";
import ContactNexus from "./ContactNexus";
import LevelMeter from "./LevelMeter";
import UserSearchBar from "./UserSearchBar";
import SiteFooter from "./SiteFooter";
import { useState, useEffect } from "react";

const navLink = ({ isActive }) =>
  `text-[11px] font-display tracking-[0.24em] uppercase transition-colors ${
    isActive ? "text-nexus-cyan" : "text-black/75 hover:text-black"
  }`;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const onWatch = loc.pathname.startsWith("/watch");
  // Live-stream views (viewer AND streamer POV) -- the floating support
  // widget sat on top of the live chat/controls on every one of these. A
  // dedicated inline instance lives in the Studio Dashboard header instead.
  const onLiveView = onWatch || loc.pathname.startsWith("/feed") || loc.pathname.startsWith("/studio");
  const isLanding = loc.pathname === "/";
  const isOwner = user?.role === "owner";

  // Apply light theme to body on every route except landing (which locks dark/black)
  useEffect(() => {
    const b = document.body;
    b.classList.remove("theme-light", "theme-dark-lock");
    if (isLanding) b.classList.add("theme-dark-lock");
    else b.classList.add("theme-light");
    return () => { b.classList.remove("theme-light", "theme-dark-lock"); };
  }, [isLanding]);
  // ED3N AI is a back-office tool only — restricted to authenticated dashboard routes.
  // Hidden from public pages: /, /explore, /watch, /feed, /u/:id, /library, /coins, /pricing, /quests, /verify, /t/:handle, /auth, /messages, /wallet
  const DASHBOARD_PREFIXES = ["/owner", "/studio", "/staff", "/creator", "/sponsor", "/parental"];
  const showEden = !!user && DASHBOARD_PREFIXES.some((p) => loc.pathname.startsWith(p));
  const [viewMode, setViewMode] = useState("owner");
  // Owner can flip the dashboard view between owner/creator/viewer

  const switchView = (m) => {
    setViewMode(m);
    if (m === "owner") nav("/owner");
    if (m === "creator") nav("/studio");
    if (m === "viewer") nav("/");
  };

  return (
    <div className="min-h-screen text-white nexus-bg-noise relative">
      <PasswordGate />
      <header className="sticky top-0 z-40 glass-strong border-b border-white/10" data-testid="nexus-nav">
        <div className="max-w-[1500px] mx-auto px-3 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <span className="w-1.5 h-1.5 rounded-full bg-nexus-gold group-hover:bg-nexus-pink transition-colors" />
            <span className="text-[10px] tracking-[0.5em] text-white/30 group-hover:text-white/70 font-display uppercase">Home</span>
          </Link>
          <nav className={`hidden lg:flex items-center gap-6 flex-1 min-w-0 overflow-x-auto no-scrollbar ${isLanding ? "!hidden" : ""}`} data-testid="main-nav">
            <NavLink to="/" className={navLink} data-testid="nav-explore"><Compass className="w-3.5 h-3.5 inline mr-1.5" />Explore</NavLink>
            <NavLink to="/library" className={navLink} data-testid="nav-library"><Tv className="w-3.5 h-3.5 inline mr-1.5" />Library</NavLink>
            <NavLink to="/coins" className={navLink} data-testid="nav-coins"><Sparkles className="w-3.5 h-3.5 inline mr-1.5" />Coins</NavLink>
            <NavLink to="/quests" className={navLink} data-testid="nav-quests"><Trophy className="w-3.5 h-3.5 inline mr-1.5" />Quests</NavLink>
            <NavLink to="/pricing" className={navLink} data-testid="nav-pricing">Tiers</NavLink>
            <NavLink to="/marketplace" className={navLink} data-testid="nav-marketplace"><Briefcase className="w-3.5 h-3.5 inline mr-1.5" />Marketplace</NavLink>
            {user && <NavLink to={`/u/${user.id}`} className={navLink} data-testid="nav-my-profile"><UserIcon className="w-3.5 h-3.5 inline mr-1.5" />My profile</NavLink>}
            {user && <NavLink to="/dm" className={navLink} data-testid="nav-dm"><MessageCircle className="w-3.5 h-3.5 inline mr-1.5" />Messages</NavLink>}
            {user && <NavLink to="/friends" className={navLink} data-testid="nav-friends"><UserIcon className="w-3.5 h-3.5 inline mr-1.5" />Friends</NavLink>}
            {user && <NavLink to="/business" className={navLink} data-testid="nav-business"><Megaphone className="w-3.5 h-3.5 inline mr-1.5" />Business</NavLink>}
            {(user?.role === "creator" || user?.role === "owner") && <NavLink to="/studio" className={navLink} data-testid="nav-studio"><Radio className="w-3.5 h-3.5 inline mr-1.5" />Studio</NavLink>}
            {(user?.role === "staff" || user?.role === "owner") && <NavLink to="/staff" className={navLink} data-testid="nav-staff">Staff</NavLink>}
            {isOwner && <NavLink to="/owner" className={navLink} data-testid="nav-owner"><Crown className="w-3.5 h-3.5 inline mr-1.5" />Owner HQ</NavLink>}
            {user?.role === "sponsor" && <NavLink to="/sponsor" className={navLink} data-testid="nav-sponsor">Sponsor</NavLink>}
            {user?.role === "owner" && <NavLink to="/sponsor" className={navLink} data-testid="nav-sponsor-owner">Sponsor HQ</NavLink>}
            {user && !user.is_child_account && <NavLink to="/parental" className={navLink} data-testid="nav-parental"><Shield className="w-3.5 h-3.5 inline mr-1.5" />Parental</NavLink>}
            <NavLink to="/wallet" className={navLink} data-testid="nav-wallet"><Wallet className="w-3.5 h-3.5 inline mr-1.5" />Wallet</NavLink>
            {user && <NavLink to="/settings" className={navLink} data-testid="nav-settings"><Settings className="w-3.5 h-3.5 inline mr-1.5" />Settings</NavLink>}
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-3">
            {!isLanding && <UserSearchBar variant="header" />}
            {isOwner && (
              <div className="hidden md:flex items-center gap-1 border border-white/10 p-0.5" data-testid="mode-switcher">
                {[
                  { id: "owner", icon: Crown, color: "#FFC107" },
                  { id: "creator", icon: Radio, color: "#FF007F" },
                  { id: "viewer", icon: Eye, color: "#00F0FF" },
                ].map((m) => (
                  <button key={m.id} onClick={() => switchView(m.id)} data-testid={`mode-${m.id}`} title={`View as ${m.id}`}
                    className={`p-1.5 transition-all ${viewMode === m.id ? "bg-white/10" : "hover:bg-white/5 text-white/40"}`}
                    style={viewMode === m.id ? { color: m.color } : undefined}>
                    <m.icon className="w-3.5 h-3.5" />
                  </button>
                ))}
              </div>
            )}
            {user ? (
              <>
                <div className="flex flex-col items-end leading-tight">
                  <span className="text-[8px] sm:text-[11px] font-display tracking-wider whitespace-nowrap">{user.display_name}</span>
                  <span className="text-[6px] sm:text-[9px] text-nexus-gold tracking-[0.15em] sm:tracking-[0.3em] uppercase whitespace-nowrap">{user.role} · {(user.wallet_balance||0).toFixed(0)} NX</span>
                </div>
                <Link to={`/u/${user.id}`} title="Your profile"><img src={user.avatar} alt="" className="w-9 h-9 ring-1 ring-white/20 object-cover cursor-pointer hover:ring-nexus-cyan transition" data-testid="user-avatar" /></Link>
                <button onClick={() => { logout(); nav("/"); }} className="text-white/50 hover:text-white" data-testid="logout-btn" title="Sign out" aria-label="Sign out"><LogOut className="w-4 h-4" /></button>
              </>
            ) : (
              <>
                <Link to="/auth" className="btn-ghost !py-2 !px-4 text-[10px]" data-testid="nav-signin">Sign in</Link>
                <Link to="/auth?mode=signup" className="btn-nexus !py-2.5 !px-5" data-testid="nav-signup">Sign up</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className={onWatch ? "" : "px-6 lg:px-10 py-10"}>{children}</main>

      {!onWatch && <SiteFooter />}

      {showEden && <EdenChat />}
      {!onLiveView && <ContactNexus />}
      {/* BoostedFeedOverlay retired -- it duplicated the same real 40/40/20 ad system
          (db.ad_campaigns via /business/ad/*) that SponsorBanner now correctly calls
          inline on profile/live-stream/reel surfaces. Running both simultaneously risked
          double-counting the same viewer's exposure to a campaign (each fires its own
          /business/ad/impression with a potentially different creator_id, since this
          overlay could only ever infer creator context from the URL, not the actual
          page's own state), which would unfairly dilute other participants' pro-rata
          share at settlement. */}
    </div>
  );
}
